/** Parse Frontier RCT3 park/track .dat archives (non-OVL). Format notes: Belgabor + Joeywp binaryloader.js */

export interface VarDecl {
  name: string;
  dtype: string;
  size: number;
  sub: VarDecl[];
}

export interface ClassDecl {
  name: string;
  vars: VarDecl[];
}

export type Rct3Value =
  | number
  | boolean
  | string
  | number[]
  | Rct3Value[]
  | { [key: string]: Rct3Value }
  | { _opaque: string; _size: number }
  | { _unknown: string; _size: number };

export interface Rct3Object {
  _class: string;
  _classid: number;
  _ref: number;
  _error?: string;
  [key: string]: Rct3Value | string | number | undefined;
}

const OPAQUE_LENGTH_PREFIXED = new Set([
  'GE_Terrain',
  'SkirtTrees',
  'WaterManager',
  'PathTileList',
  'flexicachelist',
  'waypointlist',
  'managedImage',
  'graphedValue',
  'BlockingScenery',
  'pathnodearray',
  'resourcesymbol',
]);

class Reader {
  o = 0;

  constructor(private data: Uint8Array) {}

  tell(): number {
    return this.o;
  }

  seek(o: number): void {
    this.o = o;
  }

  remaining(): number {
    return this.data.length - this.o;
  }

  private view(): DataView {
    return new DataView(this.data.buffer, this.data.byteOffset, this.data.byteLength);
  }

  u8(): number {
    return this.data[this.o++];
  }

  i8(): number {
    const v = this.view().getInt8(this.o);
    this.o += 1;
    return v;
  }

  u16(): number {
    const v = this.view().getUint16(this.o, true);
    this.o += 2;
    return v;
  }

  u32(): number {
    const v = this.view().getUint32(this.o, true);
    this.o += 4;
    return v;
  }

  i32(): number {
    const v = this.view().getInt32(this.o, true);
    this.o += 4;
    return v;
  }

  /** RCT3 refs fit in the safe-integer range for park files we care about. */
  u64(): number {
    const v = this.view().getBigUint64(this.o, true);
    this.o += 8;
    return Number(v);
  }

  f32(): number {
    const v = this.view().getFloat32(this.o, true);
    this.o += 4;
    return v;
  }

  bytes(n: number): Uint8Array {
    const v = this.data.subarray(this.o, this.o + n);
    this.o += n;
    return v;
  }

  pascal16(): string {
    const n = this.u16();
    return new TextDecoder('ascii').decode(this.bytes(n));
  }

  string32(): string {
    const length = this.u32();
    const raw = this.bytes(length);
    if (length <= 4) return '';
    return new TextDecoder('utf-16le').decode(raw.subarray(4)).replace(/\0+$/, '');
  }
}

function parseVarDecl(r: Reader): VarDecl {
  const name = r.pascal16();
  const dtype = r.pascal16();
  const size = r.u32();
  const subn = r.u32();
  const sub = Array.from({ length: subn }, () => parseVarDecl(r));
  return { name, dtype, size, sub };
}

export function parseArchive(data: Uint8Array): {
  classes: ClassDecl[];
  blocks: Rct3Object[];
  end: number;
} {
  const r = new Reader(data);
  const magicA = r.u32();
  const magicB = r.u32();
  if (magicA === 0 && magicB === 0) {
    const expansion = r.u8();
    r.u8();
    r.u8();
    r.u8();
    const first = r.u32();
    const headerUnknownCount = first / 4 - 4;
    for (let i = 0; i < headerUnknownCount; i++) r.u32();
    if (expansion & (1 << 5)) {
      r.i32();
      r.i32();
      r.i32();
      r.i32();
    }
  } else {
    r.seek(0);
  }

  const classCount = r.u32();
  const classes: ClassDecl[] = [];
  for (let i = 0; i < classCount; i++) {
    const cname = r.pascal16();
    const vcount = r.u32();
    const vars = Array.from({ length: vcount }, () => parseVarDecl(r));
    classes.push({ name: cname, vars });
  }

  const readValue = (decl: VarDecl): Rct3Value => {
    const dt = decl.dtype;
    if (dt === 'int32') return r.i32();
    if (dt === 'uint32') return r.u32();
    if (dt === 'float32') return r.f32();
    if (dt === 'bool') return r.i8() !== 0;
    if (dt === 'int8' || dt === 'uint8') return r.u8();
    if (dt === 'matrix44') return Array.from({ length: 16 }, () => r.f32());
    if (dt === 'vector3' || dt === 'orientation') return [r.f32(), r.f32(), r.f32()];
    if (dt === 'managedobjectptr' || dt === 'reference') return r.u64();
    if (dt === 'string' || dt === 'resourcesymbol') return r.string32();
    if (OPAQUE_LENGTH_PREFIXED.has(dt)) {
      const dataSize = r.u32();
      const start = r.tell();
      r.bytes(dataSize);
      r.seek(start + dataSize);
      return { _opaque: dt, _size: dataSize };
    }
    if (dt === 'struct') {
      const dataSize = decl.size !== 0 ? decl.size : r.u32();
      const start = r.tell();
      const obj: Record<string, Rct3Value> = {};
      for (const sub of decl.sub) obj[sub.name] = readValue(sub);
      r.seek(start + dataSize);
      return obj;
    }
    if (dt === 'array' || dt === 'list') {
      const dataSize = r.u32();
      const start = r.tell();
      const arrayLength = r.u32();
      const items: Rct3Value[] = [];
      for (let i = 0; i < arrayLength; i++) {
        if (
          decl.sub.length === 1 &&
          !['struct', 'array', 'list'].includes(decl.sub[0].dtype) &&
          decl.sub[0].sub.length === 0
        ) {
          items.push(readValue(decl.sub[0]));
        } else {
          const item: Record<string, Rct3Value> = {};
          for (const sub of decl.sub) item[sub.name] = readValue(sub);
          items.push(item);
        }
      }
      r.seek(start + dataSize);
      return items;
    }
    if (decl.size > 0) {
      r.bytes(decl.size);
      return '';
    }
    const dataSize = r.u32();
    const start = r.tell();
    r.bytes(Math.min(dataSize, r.remaining()));
    r.seek(start + dataSize);
    return { _unknown: dt, _size: dataSize };
  };

  const protoCount = r.u32();
  const blocks: Rct3Object[] = [];
  for (let bi = 0; bi < protoCount; bi++) {
    const classid = r.u32();
    const ref = r.u64();
    const cdecl = classes[classid];
    const obj: Rct3Object = {
      _class: cdecl.name,
      _classid: classid,
      _ref: ref,
    };
    try {
      for (const v of cdecl.vars) obj[v.name] = readValue(v);
    } catch (e) {
      obj._error = `${e instanceof Error ? e.name : 'Error'}: ${e instanceof Error ? e.message : e} @ 0x${r.tell().toString(16)}`;
      blocks.push(obj);
      return { classes, blocks, end: r.tell() };
    }
    blocks.push(obj);
  }

  return { classes, blocks, end: r.tell() };
}
